import { NestedTreeControl } from '@angular/cdk/tree';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  HostListener,
  Inject,
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTreeModule, MatTreeNestedDataSource } from '@angular/material/tree';

import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PrefundCurrencyPipe } from 'src/app/prefund-currency.pipe';
import { PeriodService } from 'src/app/services/period.service';
import { AuthService } from 'src/services/auth/auth.service';
import { TagNode } from 'src/services/tag-tree/TagNode';
import { DetailedTag } from 'src/services/tag/DetailedTag';
import { Summary, Tag, TagPeriod } from 'src/services/tag/Tag';
import { TagService } from 'src/services/tag/tag.service';
import { UserService } from 'src/services/user/user.service';
import { archiveSelected } from 'src/utils/archiveSelected';
import { duplicateSelected } from 'src/utils/duplicateSelected';
import { getRelPathCopy } from 'src/utils/getRelPathCopy';
import { getTagColor } from 'src/utils/getTagColor';
import { getTagSummary } from 'src/utils/getTagSummary';
import { getTopLevelTag } from 'src/utils/getTopLevelTag';
import { hasChildren } from 'src/utils/hasChildren';
import { isParent } from 'src/utils/isParent';
import { isVisible } from 'src/utils/isVisible';
import { move } from 'src/utils/move';
import { getSummaryIcon } from 'src/utils/getSummaryIcon';
import { trackByTagId } from 'src/utils/trackByTagId';
import { WelcomeToThePlannerComponent } from '../../app/components/welcome-to-the-planner/welcome-to-the-planner.component';
import { EnvironmentService } from 'src/app/services/environment.service';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatButtonModule } from '@angular/material/button';
import { LocalSettingsService } from 'src/app/services/local-settings.service';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { PlannerEmptyStateComponent } from 'src/app/components/planner-empty-state/planner-empty-state.component';
import { ProBadgeComponent } from 'src/app/components/pro-badge/pro-badge.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

export interface PlannerData {
  tags: DetailedTag[];
  archivedTags: DetailedTag[];
  preferredTagOrder: string[];
  autoFocusTagId?: string;
}

@Component({
  selector: 'planner',
  templateUrl: './planner.component.html',
  standalone: true,
  imports: [
    MatIconModule,
    CommonModule,
    MatTreeModule,
    MatDialogModule,
    MatSnackBarModule,
    MatMenuModule,
    MatTooltipModule,
    MatCheckboxModule,
    PlannerEmptyStateComponent,
    PrefundCurrencyPipe,
    MatExpansionModule,
    MatButtonModule,
    MatSelectModule,
    ProBadgeComponent,
    MatProgressSpinner,
  ],
  styleUrls: ['./planner.component.scss'],
})
export class PlannerComponent {
  oldTags: DetailedTag[] = [];
  newTags: DetailedTag[] = [];

  state: 'plan-editor' | 'plan-tutorial' = 'plan-editor';

  dataSource = new MatTreeNestedDataSource<TagNode>();
  treeControl = new NestedTreeControl<TagNode>((node) => node.children);
  MAX_LEVEL = 4;

  getTagSummary = getTagSummary;
  getTagColor = getTagColor;
  trackByTagId = trackByTagId;

  isSaving = false;

  public readonly defaultCellWidth = '350px';
  public readonly defaultMiniCellWidth = '165px';

  constructor(
    @Inject(MAT_DIALOG_DATA) public injectedData: PlannerData,
    private matDialogRef: MatDialogRef<PlannerComponent>,
    private tagService: TagService,
    public userService: UserService,
    private _snackBar: MatSnackBar,
    private changeDetectorRef: ChangeDetectorRef,
    public authService: AuthService,
    public periodService: PeriodService,
    public environmentService: EnvironmentService,
    public localSettingsService: LocalSettingsService
  ) {}

  ngOnInit(): void {
    this.state = 'plan-editor';

    this.oldTags = JSON.parse(
      JSON.stringify([
        ...this.injectedData.tags,
        ...this.injectedData.archivedTags,
      ])
    );

    this.newTags = JSON.parse(
      JSON.stringify([
        ...this.injectedData.tags,
        ...this.injectedData.archivedTags,
      ])
    );

    if (this.injectedData.autoFocusTagId) {
      const autofocusTag = this.oldTags.find(
        (t) => t.id === this.injectedData.autoFocusTagId
      );

      const autoFocusTopLevelTag = getTopLevelTag(autofocusTag!, this.newTags);

      const autofocusElementIdPrefix =
        autoFocusTopLevelTag?.summary === Summary.expenditureProgress
          ? 'planned-expenditure'
          : 'planned-income';
      const autoFocusElementId = `${autofocusElementIdPrefix}-${autofocusTag?.id}`;

      setTimeout(() => {
        document.getElementById(autoFocusElementId)?.focus();
      });
    }
  }

  deleteTag(tag: Tag) {
    const confirmed = confirm('Permanently delete this tag?');
    if (!confirmed) return;

    if (!this.hasChildren(tag)) {
      this.newTags = this.newTags.filter((t) => t.fullPath !== tag.fullPath);
    } else {
      const confirmed = confirm('Delete tag with all its children?');
      if (confirmed) {
        this.newTags = this.newTags.filter(
          (t) =>
            !isParent(tag.fullPath, t.fullPath) && t.fullPath !== tag.fullPath
        );
      }
    }
  }

  getRelPathCopy(fullPath: string): string {
    return getRelPathCopy(fullPath, this.newTags);
  }

  duplicate(tag: DetailedTag) {
    this.newTags = duplicateSelected(tag, this.newTags, this.tagService);
  }

  archive(tag: DetailedTag, isArchived = true) {
    const tagsWithUpdatedArchiveState = archiveSelected(
      tag,
      this.newTags,
      isArchived
    );
    if (tagsWithUpdatedArchiveState) {
      this.newTags = tagsWithUpdatedArchiveState as DetailedTag[];
    }
  }

  getColorClassFor(tag: Tag) {
    return PlannerComponent.getColorClass(tag, this.newTags);
  }

  static getColorClass(tag: Tag, tags: Tag[]) {
    const topLevelTag = tag.fullPath.split('/')[0];
    const topLevelColor = tags.find((t) => t.fullPath === topLevelTag)?.color;
    const color = !tag.fullPath ? tag.color : topLevelColor;

    switch (color) {
      case 'green':
        return 'bg-green-200 !border-green-400';
      case 'yellow':
        return 'bg-yellow-200 !border-yellow-400';
      case 'blue':
        return 'bg-blue-200 !border-blue-400';
      case 'red':
        return 'bg-red-200 !border-red-400';
      case 'purple':
        return 'bg-purple-200 !border-purple-400';
      case 'white':
        return 'bg-stone-200 !border-stone-400';
      case 'orange':
        return 'bg-orange-200 !border-orange-400';
      default:
        return 'bg-green-200 !border-green-400';
    }
  }

  getTopLevelTag(tag: Tag): Tag | undefined {
    return getTopLevelTag(tag, this.newTags);
  }

  hasDiff() {
    const oldTags = JSON.stringify(this.oldTags);
    const newTags = JSON.stringify(this.newTags);

    return oldTags !== newTags;
  }

  closeWithConfirmationIfNeeded() {
    const hasChanges = this.hasDiff();
    const canClose =
      !hasChanges || confirm('Dismiss changes and close planner?');
    if (canClose) {
      this.matDialogRef.close();
    }
  }

  trackByFn(_index: number, item: TagNode) {
    return item.fullPath;
  }

  hasChild = (_: number, node: TagNode) =>
    !!node.children && node.children.length > 0;

  isTopLevel(node: Tag) {
    return !node.fullPath.includes('/');
  }

  hasChildren(tag: Tag) {
    return hasChildren(this.newTags, tag);
  }

  updateNameAndChildrenName(tag: Tag, event: Event) {
    const target = event.target as HTMLInputElement;

    const oldFullPath = tag.fullPath;

    const newRelPath = target.value.replace(' ', '-').toLowerCase();

    const newRelPathWIthoutSpecialChars = newRelPath.replace(
      /[^a-zA-Z0-9-]/g,
      ''
    );

    const newFullPath = [
      ...tag.fullPath.split('/').slice(0, -1),

      newRelPathWIthoutSpecialChars,
    ].join('/');

    this.newTags = this.newTags.map((t) => {
      if (isParent(oldFullPath, t.fullPath) || t.id === tag.id) {
        return {
          ...t,
          fullPath: t.fullPath.replace(oldFullPath, newFullPath),
        };
      }
      return t;
    });
    // }

    return event;
  }

  updateColor(tag: Tag, event: Event) {
    tag.color = (event.target as HTMLInputElement).value;
  }

  updateSummary(tag: Tag, event: MatSelectChange) {
    tag.summary = event.value as Summary;
  }

  getPlannedExpenditure(tag: Tag) {
    const expenditure = !tag.isPeriodic
      ? tag.plannedExpenditure ?? 0
      : tag.periods?.find(
          (p) =>
            p.period.month === this.periodService.current.month &&
            p.period.year === this.periodService.current.year
        )?.plannedExpenditure ?? 0;

    return expenditure;
  }

  getPlannedIncome(tag: Tag) {
    return !tag.isPeriodic
      ? tag.plannedIncome ?? 0
      : tag.periods?.find(
          (p) =>
            p.period.month === this.periodService.current.month &&
            p.period.year === this.periodService.current.year
        )?.plannedIncome ?? 0;
  }

  setPeriodicPlannedIncome(tag: Tag, value: number) {
    const period = tag.periods?.find(
      (p) =>
        p.period.month === this.periodService.current.month &&
        p.period.year === this.periodService.current.year
    );

    if (period) {
      period.plannedIncome = value;
    } else {
      tag.periods?.push({
        period: {
          month: this.periodService.current.month,
          year: this.periodService.current.year,
        },
        plannedIncome: value,
      });
    }
  }

  setPeriodicPlannedExpenditure(tag: Tag, value: number) {
    const period = tag.periods?.find(
      (p) =>
        p.period.month === this.periodService.current.month &&
        p.period.year === this.periodService.current.year
    );

    if (period) {
      period.plannedExpenditure = value;
    } else {
      tag.periods?.push({
        period: {
          month: this.periodService.current.month,
          year: this.periodService.current.year,
        },
        plannedExpenditure: value,
      });
    }
  }

  getChildrenTotal(tag: Tag): number {
    const summary = this.getTopLevelTag(tag)?.summary;
    const childrenTags = this.newTags.filter(
      (t) =>
        t.fullPath.startsWith(`${tag.fullPath}/`) &&
        tag.fullPath.split('/').length + 1 === t.fullPath.split('/').length && // direct children only
        t.fullPath !== tag.fullPath
    );

    if (!tag.isPeriodic) {
      return childrenTags
        .map((t) =>
          summary === Summary.expenditureProgress
            ? t.plannedExpenditure
            : t.plannedIncome
        )
        .reduce((acc, val) => acc! + val!, 0)!;
    } else {
      // get periods
      const current = this.periodService.current;

      const periodicChildrenTotal = childrenTags
        .map((child) => {
          const period = child.periods?.find(
            (p) =>
              p.period.month === current.month && p.period.year === current.year
          );

          return summary === Summary.expenditureProgress
            ? period?.plannedExpenditure ?? 0
            : period?.plannedIncome ?? 0;
        })
        .reduce((acc, val) => acc! + val!, 0);

      return periodicChildrenTotal;
    }
  }

  hasPreferredOrderDiff(): boolean {
    return (
      JSON.stringify(this.newTags.map((t) => t.id)) !==
      JSON.stringify(this.injectedData.preferredTagOrder)
    );
  }

  async save() {
    try {
      this.isSaving = true;
      if (this.hasPreferredOrderDiff()) {
        const newTagIds: string[] = this.newTags
          .map((t) => t.id)
          .filter((id) => id !== undefined) as string[];
        this.userService.savePreferredTagOrder(newTagIds);
      }

      if (this.hasDiff()) {
        const errorPlanMessage = this.getPlanErrorMessage();
        if (!errorPlanMessage) {
          await this.tagService.save(this.newTags);
          this.matDialogRef.close({ sidebarShouldRefresh: true });
          this._snackBar.open('Plan saved');
        } else {
          this._snackBar.open(`Error: ${errorPlanMessage}`);
        }
      } else {
        this.closeWithConfirmationIfNeeded();
      }
    } finally {
      this.isSaving = false;
    }
  }

  // Validate form
  getPlanErrorMessage(): string | null {
    for (const tag of this.newTags) {
      if (tag.plannedExpenditure && tag.plannedExpenditure < 0) {
        return 'Planned expenditure cannot be negative';
      }
      if (tag.plannedIncome && tag.plannedIncome! < 0) {
        return 'Planned income cannot be negative';
      }
      if (this.hasEmptyTag()) {
        return 'Empty tags are not allowed';
      }
      if (this.hasDuplicate()) {
        return 'Duplicate tags are not allowed';
      }
      if (tag.fullPath.split('/').slice(-1)[0].startsWith('-')) {
        return 'Tags cannot start with -';
      }
      if (tag.fullPath.split('/').slice(-1)[0].endsWith('-')) {
        return 'Tags cannot end with -';
      }
    }
    return null;
  }

  hasEmptyTag() {
    return this.newTags.some(
      (t) => t.fullPath === '' || t.fullPath.endsWith('/')
    );
  }

  hasDuplicate() {
    const tagPaths = this.newTags.map((t) => t.fullPath);
    return new Set(tagPaths).size !== tagPaths.length;
  }

  updatePlanned(tag: Tag, event: Event) {
    const target = event.target as HTMLInputElement;
    const value = parseFloat(target.value);
    if (isNaN(value)) {
      return;
    }

    if (!getTopLevelTag(tag, this.newTags)?.isPeriodic) {
      if (getTagSummary(tag, this.newTags) === Summary.expenditureProgress) {
        tag.plannedExpenditure = value;
      } else if (getTagSummary(tag, this.newTags) === Summary.incomeProgress) {
        tag.plannedIncome = value;
      }
    } else {
      const existingTagPeriod = tag.periods?.find(
        (p) =>
          p.period.year === this.periodService.current.year &&
          p.period.month === this.periodService.current.month
      );
      if (existingTagPeriod) {
        if (getTagSummary(tag, this.newTags) === Summary.expenditureProgress) {
          existingTagPeriod.plannedExpenditure = value;
        } else {
          existingTagPeriod.plannedIncome = value;
        }
      } else {
        tag.periods?.push({
          period: {
            year: this.periodService.current.year,
            month: this.periodService.current.month,
          },
          plannedExpenditure:
            getTagSummary(tag, this.newTags) === Summary.expenditureProgress
              ? value
              : 0,
          plannedIncome:
            getTagSummary(tag, this.newTags) === Summary.incomeProgress
              ? value
              : 0,
        });
      }
    }

    this.changeDetectorRef.detectChanges();
  }

  getTagPeriod(tag: Tag): TagPeriod | null {
    const current = this.periodService.current;
    const currentTagPeriod = tag.periods?.find(
      (p) => p.period.month === current.month && p.period.year === current.year
    );
    return currentTagPeriod ?? null;
  }

  addTag(isPeriodic = false) {
    const newTag: DetailedTag = {
      fullPath: '',
      color: 'green',
      plannedExpenditure: 0,
      plannedIncome: 0,
      isPeriodic,
      periods: isPeriodic ? [] : undefined,
      isArchived: false,
      id: this.tagService.getNewId(),
      summary: Summary.expenditureProgress,
      actualExpenditure: 0,
      actualIncome: 0,
      runningBalance: 0,
    };
    this.newTags.push(newTag);

    setTimeout(() => {
      document.getElementById('newTag')?.focus();
    });
  }

  getTagLevel(tag: Tag): number {
    return tag.fullPath.split('/').length;
  }

  getTagClass(tag: DetailedTag) {
    return tag.isArchived
      ? 'archived-row-name'
      : `pl-${(this.getTagLevel(tag) - 1) * 8}`;
  }

  addChild(tag: DetailedTag, isPeriodic = false) {
    if (!tag.fullPath || tag.fullPath.endsWith('/')) return;

    if (this.getTagLevel(tag) >= this.MAX_LEVEL) {
      this._snackBar.open('Max level reached');
      return;
    }

    const newTag: DetailedTag = {
      fullPath: `${tag.fullPath}/`,
      color: tag.color!,
      plannedExpenditure: 0,
      plannedIncome: 0,
      isPeriodic,
      periods: isPeriodic ? [] : undefined,
      id: this.tagService.getNewId(),
      isArchived: false,
      actualExpenditure: 0,
      actualIncome: 0,
      runningBalance: 0,
      summary: tag.summary ?? Summary.none,
    };

    this.newTags.splice(this.newTags.indexOf(tag) + 1, 0, newTag);

    setTimeout(() => {
      document.getElementById(newTag.fullPath)?.focus();
    }, 0);
  }

  isTopLevelNodeSummaryType(summaryType: string, node: Tag): boolean {
    return true;
  }

  @HostListener('document:keyup', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    switch (event.key) {
      case 'Escape':
        if (this.state === 'plan-tutorial') {
          this.state = 'plan-editor';
        } else {
          this.closeWithConfirmationIfNeeded();
        }
        break;
      case 'Enter':
        if (this.state === 'plan-editor') {
          this.save();
        }
    }
  }

  moveDown(tag: DetailedTag) {
    this.newTags = [...move(this.newTags, tag, 'down')];
  }

  moveUp(tag: DetailedTag) {
    this.newTags = [...move(this.newTags, tag, 'up')];
  }

  getRowClass(tag: any): any {
    const targetTag = !tag.fullPath ? tag : getTopLevelTag(tag, this.newTags);
    return `hover:bg-${targetTag?.color}-100`;
  }

  getNewFixedTags(tags: DetailedTag[]): DetailedTag[] {
    return tags
      .filter((t) => !t.isArchived)
      .filter(
        (t) => !t.isPeriodic && !getTopLevelTag(t, this.newTags)?.isPeriodic
      );
  }

  getPeriodicNewTags(tags: DetailedTag[]): DetailedTag[] {
    return tags
      .filter((t) => !t.isArchived)
      .filter(
        (t) => !!t.isPeriodic || !!getTopLevelTag(t, this.newTags)?.isPeriodic
      );
  }

  getArchivedNewTags(tags: DetailedTag[]): DetailedTag[] {
    return tags.filter((t) => t.isArchived);
  }

  hasArchivedNewTag() {
    return this.newTags.filter((t) => t.isArchived).length > 0;
  }

  isLimitedByProFeatures() {
    const hasProColors = this.newTags.some(
      (t) =>
        !this.authService.FREE_LIMITATIONS.availableColors.includes(
          getTagColor(t, this.newTags)
        )
    );

    const hasPeriodicTags = this.getPeriodicNewTags(this.newTags).length > 0;
    const hasArchivedTags = this.getArchivedNewTags(this.newTags).length > 0;

    return hasProColors || hasPeriodicTags || hasArchivedTags;
  }

  openUpsell() {
    this.matDialogRef.close({ openUpsell: true });
  }

  toggleVisibility(tag: Tag) {
    const period = tag.periods?.find(
      (p) =>
        p.period.month === this.periodService.current.month &&
        p.period.year === this.periodService.current.year
    );
    if (period) {
      period.isHidden = !period.isHidden;
    } else {
      tag.periods?.push({
        period: this.periodService.current,
        isHidden: true,
      });
    }
  }

  isVisible(tag: Tag) {
    return isVisible(tag, this.newTags, this.periodService.current);
  }

  public expandFixedTags =
    this.localSettingsService.settings.plannerExpandFixedTags;
  public expandPeriodicTags =
    this.localSettingsService.settings.plannerExpandPeriodicTags;
  public expandArchivedTags =
    this.localSettingsService.settings.plannerExpandArchivedTags;

  toggleFixedTags() {
    this.expandFixedTags = !this.expandFixedTags;
    this.localSettingsService.set({
      ...this.localSettingsService.settings,
      plannerExpandFixedTags: this.expandFixedTags,
    });
  }
  togglePeriodicTags() {
    this.expandPeriodicTags = !this.expandPeriodicTags;
    this.localSettingsService.set({
      ...this.localSettingsService.settings,
      plannerExpandPeriodicTags: this.expandPeriodicTags,
    });
  }

  toggleArchivedTags() {
    this.expandArchivedTags = !this.expandArchivedTags;
    this.localSettingsService.set({
      ...this.localSettingsService.settings,
      plannerExpandArchivedTags: this.expandArchivedTags,
    });
  }

  getSummaryIcon = getSummaryIcon;
}
